import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { BriefResponseDto, BriefService } from '@/app/services/brief/brief.service';
import { ImageService } from '@/app/services/image/image.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { EmptyStateComponent } from '@/app/atoms/empty-state/empty-state.component';
import { ThemeService } from '@/app/services/theme/theme.service';
import { formatDistanceToNow } from 'date-fns';

@Component({
  selector: 'ins-brief-list',
  imports: [RouterOutlet, EmptyStateComponent],
  templateUrl: './brief-list.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefList implements OnInit {
  private static readonly PAGE_SIZE = 25;
  private static readonly SCROLL_LOAD_THRESHOLD_PX = 80;

  allBriefs = signal<BriefResponseDto[] | undefined>(undefined);
  briefsLoading = signal(false);
  briefsLoadingMore = signal(false);
  searchQuery = signal('');
  selectedBriefId = signal<string | undefined>(undefined);

  private pageNumber = signal(0);
  private searchSessionId = signal(0);
  private searchTrigger = new Subject<string>();
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private briefService = inject(BriefService);
  private toastNotificationService = inject(ToastNotificationService);
  readonly imageService = inject(ImageService);
  readonly themeService = inject(ThemeService);

  ngOnInit(): void {
    this.syncSelectedFromRoute();

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncSelectedFromRoute());

    merge(of(''), this.searchTrigger.pipe(debounceTime(300), distinctUntilChanged()))
      .pipe(
        tap(() => {
          this.pageNumber.set(0);
          this.briefsLoadingMore.set(false);
          this.searchSessionId.update((n) => n + 1);
        }),
        switchMap((term) => {
          this.briefsLoading.set(true);
          return this.fetchBriefs(0, term).pipe(
            tap({
              next: () => this.briefsLoading.set(false),
              error: () => this.briefsLoading.set(false),
            }),
            catchError((err) => {
              console.error('Error fetching briefs:', err);
              this.toastNotificationService.alert({
                title: 'Error fetching briefs',
                message: 'Failed to retrieve briefs.',
                type: 'error',
                icon: 'error',
              });
              return of([] as BriefResponseDto[]);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.allBriefs.set(response);
      });
  }

  timeSinceCreated(createdDate: Date | string | undefined): string {
    if (createdDate == null) {
      return '—';
    }
    const d = typeof createdDate === 'string' ? new Date(createdDate) : createdDate;
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    return formatDistanceToNow(d, { addSuffix: true });
  }

  briefSubtitle(brief: BriefResponseDto): string {
    const campaign = brief.campaign?.campaignName;
    const brand = brief.businessAccount?.companyName;
    return [campaign, brand].filter(Boolean).join(' · ') || '—';
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    this.searchTrigger.next(value.trim());
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement | null;
    if (!el) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollHeight - scrollTop - clientHeight > BriefList.SCROLL_LOAD_THRESHOLD_PX) {
      return;
    }
    this.loadMoreIfNeeded();
  }

  isSelectedBrief(brief: BriefResponseDto): boolean {
    return this.selectedBriefId() === brief.id;
  }

  onBriefSelect(brief: BriefResponseDto): void {
    this.selectedBriefId.set(brief.id);
    void this.router.navigate(['/insights/brief', brief.id, 'details']);
  }

  private loadMoreIfNeeded(): void {
    if (this.briefsLoading() || this.briefsLoadingMore()) {
      return;
    }
    const list = this.allBriefs();
    if (list === undefined) {
      return;
    }

    const nextPage = this.pageNumber() + 1;
    const term = this.searchQuery().trim();
    const session = this.searchSessionId();
    this.briefsLoadingMore.set(true);

    this.fetchBriefs(nextPage, term)
      .pipe(
        catchError(() => of([] as BriefResponseDto[])),
        finalize(() => this.briefsLoadingMore.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (session !== this.searchSessionId()) {
          return;
        }
        if (response.length === 0) {
          return;
        }
        this.allBriefs.update((prev) => [...(prev ?? []), ...response]);
        this.pageNumber.set(nextPage);
      });
  }

  private fetchBriefs(page: number, searchTerm: string): Observable<BriefResponseDto[]> {
    return this.briefService.getInsightsBriefs(
      BriefList.PAGE_SIZE,
      page * BriefList.PAGE_SIZE,
      searchTerm.trim() || undefined,
    );
  }

  private syncSelectedFromRoute(): void {
    const briefId = this.route.firstChild?.snapshot.paramMap.get('briefId');
    this.selectedBriefId.set(briefId ?? undefined);
  }
}
